import { FileText, Plus, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Toaster, toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { MOCK_CASES, MOCK_STRS, type StrRecord } from '@/data/aml/mockData';
import { cn } from '@/lib/utils';

function statusBadgeClass(status: StrRecord['status']): string {
	switch (status) {
		case 'submitted':
			return 'bg-blue-50 text-blue-700 border-blue-200';
		case 'acknowledged':
			return 'bg-green-50 text-green-700 border-green-200';
		case 'rejected':
			return 'bg-red-50 text-red-700 border-red-200';
		default:
			return 'bg-yellow-50 text-yellow-700 border-yellow-200';
	}
}

function formatDate(iso: string): string {
	return new Date(iso).toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	});
}

export default function StrPage() {
	const [strs, setStrs] = useState<StrRecord[]>(MOCK_STRS);
	const [search, setSearch] = useState('');
	const [statusFilter, setStatusFilter] = useState('all');
	const [fileOpen, setFileOpen] = useState(false);
	const [page, setPage] = useState(1);
	const PAGE_SIZE = 15;

	// Form state
	const [formCaseId, setFormCaseId] = useState('');
	const [formStrType, setFormStrType] = useState('');
	const [formSubType, setFormSubType] = useState('');
	const [formAgency, setFormAgency] = useState('');
	const [formTrigger, setFormTrigger] = useState('');
	const [formNarrative, setFormNarrative] = useState('');

	const filtered = useMemo(() => {
		let result = strs;
		if (search) {
			const q = search.toLowerCase();
			result = result.filter(
				(s) =>
					s.code.toLowerCase().includes(q) ||
					s.case_code.toLowerCase().includes(q),
			);
		}
		if (statusFilter !== 'all')
			result = result.filter((s) => s.status === statusFilter);
		return result;
	}, [strs, search, statusFilter]);

	const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
	const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

	const handleFileStr = () => {
		if (!formCaseId || !formStrType || !formAgency) {
			toast.error('Fill in all required fields');
			return;
		}
		const selectedCase = MOCK_CASES.find((c) => c.id === formCaseId);
		setStrs((prev) => [
			{
				id: `str_${Date.now()}`,
				code: `STR-${String(3000 + prev.length + 1).padStart(5, '0')}`,
				case_id: formCaseId,
				case_code: selectedCase?.code ?? formCaseId,
				submission_type: formSubType || 'Initial',
				str_type: formStrType,
				status: 'submitted',
				supervising_agency: formAgency,
				filed_by: 'Alex Morgan',
				filed_at: new Date().toISOString(),
				trigger: formTrigger || 'Unusual Transaction',
			},
			...prev,
		]);
		setFileOpen(false);
		setFormCaseId('');
		setFormStrType('');
		setFormSubType('');
		setFormAgency('');
		setFormTrigger('');
		setFormNarrative('');
		toast.success('STR filed successfully');
	};

	return (
		<div className="min-h-screen bg-background">
			<Toaster richColors position="top-right" />
			<div className="border-b border-border bg-white px-6 py-4">
				<div className="flex items-center justify-between">
					<div>
						<h1 className="text-xl font-semibold text-[#0A0A0A]">
							Suspicious Transaction Reports
						</h1>
						<p className="text-sm text-[#737373] mt-0.5">
							Manage and file STRs for AML compliance
						</p>
					</div>
					<Button
						className="bg-[#0A0A0A] text-white hover:bg-[#0A0A0A]/90 gap-2"
						onClick={() => setFileOpen(true)}
					>
						<Plus className="h-4 w-4" /> File STR
					</Button>
				</div>
			</div>

			<div className="p-6 space-y-4 max-w-[1200px]">
				{/* Summary cards */}
				<div className="grid grid-cols-2 gap-4 md:grid-cols-4">
					{[
						{ label: 'Total STRs', value: strs.length },
						{
							label: 'Submitted',
							value: strs.filter((s) => s.status === 'submitted').length,
						},
						{
							label: 'Acknowledged',
							value: strs.filter((s) => s.status === 'acknowledged').length,
						},
						{
							label: 'Draft',
							value: strs.filter((s) => s.status === 'draft').length,
						},
					].map((stat) => (
						<Card key={stat.label} className="p-4 border border-[#E5E5E5]">
							<div className="text-sm text-[#737373]">{stat.label}</div>
							<div className="text-3xl font-bold text-[#0A0A0A] mt-1">
								{stat.value}
							</div>
						</Card>
					))}
				</div>

				{/* Filters */}
				<div className="flex flex-wrap items-center gap-3">
					<div className="relative flex-1 min-w-[220px] max-w-sm">
						<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
						<Input
							value={search}
							onChange={(e) => {
								setSearch(e.target.value);
								setPage(1);
							}}
							placeholder="Search by STR code or case..."
							className="pl-9 h-9 shadow-sm"
						/>
					</div>
					<Select
						value={statusFilter}
						onValueChange={(v) => {
							setStatusFilter(v);
							setPage(1);
						}}
					>
						<SelectTrigger className="w-[150px] h-9 text-sm">
							<SelectValue placeholder="All Statuses" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All Statuses</SelectItem>
							<SelectItem value="draft">Draft</SelectItem>
							<SelectItem value="submitted">Submitted</SelectItem>
							<SelectItem value="acknowledged">Acknowledged</SelectItem>
							<SelectItem value="rejected">Rejected</SelectItem>
						</SelectContent>
					</Select>
					<span className="ml-auto text-sm text-[#737373]">
						{filtered.length} reports
					</span>
				</div>

				{/* Table */}
				<Card className="border border-[#E5E5E5] overflow-hidden">
					<Table>
						<TableHeader>
							<TableRow className="bg-[#FAFAFA]">
								<TableHead className="text-xs font-semibold text-[#737373]">
									STR Code
								</TableHead>
								<TableHead className="text-xs font-semibold text-[#737373]">
									Case
								</TableHead>
								<TableHead className="text-xs font-semibold text-[#737373]">
									Type
								</TableHead>
								<TableHead className="text-xs font-semibold text-[#737373]">
									Submission
								</TableHead>
								<TableHead className="text-xs font-semibold text-[#737373]">
									Agency
								</TableHead>
								<TableHead className="text-xs font-semibold text-[#737373]">
									Status
								</TableHead>
								<TableHead className="text-xs font-semibold text-[#737373]">
									Filed By
								</TableHead>
								<TableHead className="text-xs font-semibold text-[#737373]">
									Filed At
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{paginated.map((s) => (
								<TableRow key={s.id} className="hover:bg-[#FAFAFA]">
									<TableCell>
										<div className="flex items-center gap-1.5 text-sm font-semibold text-[#0A0A0A]">
											<FileText className="h-4 w-4 shrink-0 text-[#737373]" />
											{s.code}
										</div>
									</TableCell>
									<TableCell>
										<span className="text-sm text-[#525252]">
											{s.case_code}
										</span>
									</TableCell>
									<TableCell>
										<span className="text-sm text-[#525252]">{s.str_type}</span>
									</TableCell>
									<TableCell>
										<span className="text-xs text-[#525252]">
											{s.submission_type}
										</span>
									</TableCell>
									<TableCell>
										<span className="text-xs text-[#525252]">
											{s.supervising_agency}
										</span>
									</TableCell>
									<TableCell>
										<span
											className={cn(
												'inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium',
												statusBadgeClass(s.status),
											)}
										>
											{s.status.charAt(0).toUpperCase() + s.status.slice(1)}
										</span>
									</TableCell>
									<TableCell>
										<span className="text-sm text-[#525252]">{s.filed_by}</span>
									</TableCell>
									<TableCell>
										<span className="text-xs text-[#737373]">
											{formatDate(s.filed_at)}
										</span>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Card>

				{totalPages > 1 && (
					<div className="flex items-center justify-end gap-2">
						<Button
							variant="outline"
							size="sm"
							disabled={page <= 1}
							onClick={() => setPage((p) => p - 1)}
						>
							Previous
						</Button>
						<span className="text-sm text-[#737373]">
							Page {page} of {totalPages}
						</span>
						<Button
							variant="outline"
							size="sm"
							disabled={page >= totalPages}
							onClick={() => setPage((p) => p + 1)}
						>
							Next
						</Button>
					</div>
				)}
			</div>

			{/* File STR Dialog */}
			<Dialog open={fileOpen} onOpenChange={setFileOpen}>
				<DialogContent className="max-w-[560px]">
					<DialogHeader>
						<DialogTitle>File STR</DialogTitle>
						<DialogDescription>
							Submit a Suspicious Transaction Report to the regulator.
						</DialogDescription>
					</DialogHeader>
					<div className="grid grid-cols-2 gap-4">
						<div className="space-y-1.5 col-span-2">
							<Label>
								Case <span className="text-red-500">*</span>
							</Label>
							<Select value={formCaseId} onValueChange={setFormCaseId}>
								<SelectTrigger>
									<SelectValue placeholder="Select case" />
								</SelectTrigger>
								<SelectContent className="max-h-[200px]">
									{MOCK_CASES.filter((c) => c.status === 'closed')
										.slice(0, 20)
										.map((c) => (
											<SelectItem key={c.id} value={c.id}>
												{c.code} — {c.title.slice(0, 40)}
											</SelectItem>
										))}
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-1.5">
							<Label>
								STR Type <span className="text-red-500">*</span>
							</Label>
							<Select value={formStrType} onValueChange={setFormStrType}>
								<SelectTrigger>
									<SelectValue placeholder="Select type" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="CTR">CTR</SelectItem>
									<SelectItem value="SAR">SAR</SelectItem>
									<SelectItem value="STR-Local">STR-Local</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-1.5">
							<Label>Submission Type</Label>
							<Select value={formSubType} onValueChange={setFormSubType}>
								<SelectTrigger>
									<SelectValue placeholder="Initial" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="Initial">Initial</SelectItem>
									<SelectItem value="Amendment">Amendment</SelectItem>
									<SelectItem value="Supplemental">Supplemental</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-1.5">
							<Label>
								Supervising Agency <span className="text-red-500">*</span>
							</Label>
							<Select value={formAgency} onValueChange={setFormAgency}>
								<SelectTrigger>
									<SelectValue placeholder="Select agency" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="AMLC">AMLC</SelectItem>
									<SelectItem value="FinCEN">FinCEN</SelectItem>
									<SelectItem value="FCA">FCA</SelectItem>
									<SelectItem value="MAS">MAS</SelectItem>
									<SelectItem value="FINTRAC">FINTRAC</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-1.5">
							<Label>Trigger</Label>
							<Select value={formTrigger} onValueChange={setFormTrigger}>
								<SelectTrigger>
									<SelectValue placeholder="Select trigger" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="Unusual Transaction">
										Unusual Transaction
									</SelectItem>
									<SelectItem value="PEP Match">PEP Match</SelectItem>
									<SelectItem value="Sanctions Hit">Sanctions Hit</SelectItem>
									<SelectItem value="Structuring">Structuring</SelectItem>
									<SelectItem value="Layering">Layering</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-1.5 col-span-2">
							<Label>Narrative</Label>
							<Textarea
								value={formNarrative}
								onChange={(e) => setFormNarrative(e.target.value)}
								placeholder="Describe the suspicious activity..."
								className="min-h-[80px] resize-none"
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setFileOpen(false)}>
							Cancel
						</Button>
						<Button
							className="bg-emerald-600 text-white hover:bg-emerald-700"
							disabled={!formCaseId || !formStrType || !formAgency}
							onClick={handleFileStr}
						>
							Submit STR
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
